import { call, put, select, takeLatest } from 'redux-saga/effects'
import { getPickupAddressSelector } from '../selectors'
import {
  setAddressLine1,
  setAddressLine2,
  setAptSuiteRoom,
  setCensusTract,
  setCity,
  setCountry,
  setCounty,
  setCrossStreetDirections,
  setLocation,
  setMCD,
  setMilePostMajorRoadway,
  setOtherType,
  setReferringGPSE,
  setReferringGPSN,
  setReferringUSNG,
  setState,
  setStateIds,
  setZip,
} from '../store/pickupAddress.actions'
import { PickupAddressConstants } from '../store/pickupAddress.constants'
import { ICommonAddressesOption, IPickupAddressState } from '../store/pickupAddress.types'
import { IPickupAddressOptionsState } from '../../options/store/options.types'
import { getPickupAddressOptionsSelector } from '../../options/selectors/pickupAddressOptions.selector'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { OptionsService } from '../../options/services/options.service'
import { ICity, ICountyOption, IStateAndCountiesOptionsResponse } from '../../../common/common.types'
import { initialCommonAddressOption } from '../store/pickupAddress.reducers'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { IPcr } from '../../pcr/store/pcr.types'
import { calculateReferringUSNG } from './setCalculateReferringUSNG.saga'

export function* setRefCommonAddressValues() {
  const { commonAddress }: IPickupAddressState = yield select(getPickupAddressSelector)
  if (!commonAddress) yield call(setCommonAddressData, initialCommonAddressOption)
  else yield call(setCommonAddressData, commonAddress)
}

export function* setCommonAddressData(commonAddress: ICommonAddressesOption, pcr?: IPcr) {
  try {
    const serviceId: string = yield select(getServiceIdSelector)
    const pickupAddressOptionsState: IPickupAddressOptionsState = yield select(getPickupAddressOptionsSelector)
    const { stateOptions, countryOptions, otherTypeOptions } = pickupAddressOptionsState
    const {
      name,
      type,
      address1,
      address2,
      city,
      state,
      stateIds,
      zip,
      aptSuiteRoom,
      milePostMajorRoadway,
      crossStreet,
      county,
      country,
      censusTract,
      mcd,
      usng,
      matchlat,
      matchlon,
    } = commonAddress

    const typeOption = otherTypeOptions.find((typeOption) => typeOption.id === type) || null

    let cityObj: ICity | null = null
    if (city) cityObj = { name: city, nemsisCode: '' }

    const stateOption = stateOptions.find((stateOption) => stateOption.id === state) || null

    let countyOption: ICountyOption | null = null
    if (county && state) {
      const optionsService = new OptionsService(serviceId)

      const response: IStateAndCountiesOptionsResponse = yield call([optionsService, optionsService.getCountyOptions])
      const { entity } = response

      const filteredCounties = entity.find((obj) => obj.state === String(state))
      countyOption = filteredCounties?.counties.find((option) => option.id === county) || null
    }

    const countryOption = countryOptions.find((countryOption) => countryOption.id === country) || null

    let uSNG = usng || ''
    if (matchlat && matchlon) uSNG = yield call(calculateReferringUSNG, matchlat, matchlon)

    if (pcr?.pcrId) {
      pcr.patient = {
        ...pcr.patient,
        pickupAddress: {
          ...pcr.patient.pickupAddress,
          location: name || '',
          otherType: typeOption,
          addressLine1: address1 || '',
          addressLine2: address2 || '',
          aptSuiteRoom: aptSuiteRoom || '',
          milePostMajorRoadway: milePostMajorRoadway || '',
          crossStreetDirections: crossStreet || '',
          city: cityObj,
          state: stateOption,
          county: countyOption,
          country: countryOption,
          zip: zip || '',
          referringUSNG: uSNG,
          censusTract: censusTract || '',
          mCD: mcd || '',
          stateIds,
          referringGPSN: matchlat || '',
          referringGPSE: matchlon || '',
        },
      }
      return
    }

    yield put(setLocation(name || ''))

    yield put(setOtherType(typeOption))

    yield put(setAddressLine1(address1 || ''))
    yield put(setAddressLine2(address2 || ''))

    yield put(setAptSuiteRoom(aptSuiteRoom || ''))

    yield put(setMilePostMajorRoadway(milePostMajorRoadway || ''))

    yield put(setCrossStreetDirections(crossStreet || ''))

    yield put(setCity(cityObj))

    yield put(setState(stateOption))

    yield put(setCounty(countyOption))

    yield put(setZip(zip || ''))

    yield put(setCountry(countryOption))

    yield put(setCensusTract(censusTract || ''))

    yield put(setReferringUSNG(uSNG))

    yield put(setMCD(mcd || ''))

    yield put(setStateIds(stateIds))

    if (matchlat && matchlon) {
      yield put(setReferringGPSN(matchlat))
      yield put(setReferringGPSE(matchlon))
      yield put(setReferringUSNG(uSNG))
    } else {
      yield put(setReferringGPSN(''))
      yield put(setReferringGPSE(''))
    }
  } catch (e) {
    yield put(throwPcrException(`Pickup Address - setRefCommonAddressData() error: ${e}`))
  }
}

export function* setRefCommonAddressValuesSaga() {
  yield takeLatest([PickupAddressConstants.SET_COMMON_ADDRESS], performWithMutexHOG, setRefCommonAddressValues)
}
